import { gql, useQuery } from '@apollo/client';
import { useMutation } from '@tbitop/api';
import { useUser } from '@tbitop/authentication';
import { useGraphQLErrorHandler } from '@tbitop/error-handling';
import { defaultTo } from 'lodash-es';
import { useCallback } from 'react';
import type { EinzelErgebnis, MutationNutzerEntfernenArgs } from './__generated__/globalTypes';
import type { NutzerMandantenFragment, NutzerMandantenQuery } from './__generated__/nutzer-mandanten';

export type NutzerMandanten = NutzerMandantenFragment;

const emptyMandanten: NutzerMandanten[] = [];

export const useNutzerEntfernenMutation = () => {
  const { handleWithErrorNotification } = useGraphQLErrorHandler();
  const [executeMutation, { loading, error }] = useMutation<EinzelErgebnis[], MutationNutzerEntfernenArgs>(gql`
    mutation NutzerEntfernen($ids: [UUID!]!) {
      nutzerEntfernen(ids: $ids) {
        erfolgreich
        fehler
        id
      }
    }
  `, {
    onError: handleWithErrorNotification('Error in user deletion'),
  });

  return {
    execute: useCallback((variables: MutationNutzerEntfernenArgs) => executeMutation({ variables }), [executeMutation]),
    loading,
    error,
  };
};


export const useNutzerMandanten = () => {
  const { handleWithErrorNotification } = useGraphQLErrorHandler();
  const { data } = useQuery<NutzerMandantenQuery>(
    gql`
      query NutzerMandanten {
        nutzerMandanten {
          ...NutzerMandanten
        }
      }
      fragment NutzerMandanten on Mandant {
          id
          anzeigename
          hasSalesPlanner
          keycloakOrganisationId
      }
    `,
    {
      onError: handleWithErrorNotification('Es konnten keine Informationen zu den verfügbaren Mandanten geladen werden.'),
    },
  );

  return { mandanten: defaultTo(data?.nutzerMandanten, emptyMandanten) };
};

export const useNutzerMandant = () => {
  const { mandanten } = useNutzerMandanten();
  const user = useUser();
  return mandanten.find(({ keycloakOrganisationId }) => user?.activeOrganization.id === keycloakOrganisationId) ?? null;
};
